import { FormikProps, getIn, setNestedObjectValues } from "formik";
import { useTranslation } from "react-i18next";

/**
 * useFormikHelpers
 *
 * Provides unified helpers for Formik error and helper text handling, supporting:
 * - Standard fields
 * - Array fields (with index)
 * - Deep (recursive) error checking
 *
 * @param formik - The FormikProps object from your form.
 * @returns {Object} Helper functions for error and helper text handling.
 *
 * @example
 * // For a standard field:
 * <TextField
 *   error={handleFormikError("email")}
 *   helperText={handleFormikHelperText("email")}
 * />
 *
 * @example
 * // For an array field (e.g., addresses[0].city):
 * <TextField
 *   error={handleFormikError("city", { parent: "addresses", index: 0 })}
 *   helperText={handleFormikHelperText("city", { parent: "addresses", index: 0 })}
 * />
 *
 * @example
 * // For deep error checking (any nested error under "address" - will display the first error message found):
 * {handleFormikError("address", { deep: true }) && <span>There is an address error</span>}
 */

function buildPath(name: string, parent?: string, index?: number) {
  if (parent && typeof index === "number") {
    return `${parent}[${index}].${name}`;
  }
  return parent ? `${parent}.${name}` : name;
}

function hasAnyError(obj: any, touchedObj?: any): boolean {
  if (!obj) return false;
  if (typeof obj === "string") {
    // null = ignore touched (show error), undefined = not touched (hide error)
    if (touchedObj === null) return true;
    if (touchedObj === undefined) return false;
    return !!touchedObj;
  }
  if (typeof obj === "object") {
    return Object.keys(obj).some((key) =>
      hasAnyError(
        obj[key],
        touchedObj === null ? null : touchedObj ? touchedObj[key] : undefined,
      ),
    );
  }
  return false;
}

export function getFirstErrorMessage(obj: any): string | null {
  if (!obj) return null;
  if (typeof obj === "string") return obj;
  if (typeof obj === "object") {
    for (const key of Object.keys(obj)) {
      const result = getFirstErrorMessage(obj[key]);
      if (result) return result;
    }
  }
  return null;
}

export const useFormikHelpers = (formik: FormikProps<any>) => {
  const { t } = useTranslation();

  const validateAndTouch = async (fields?: any) => {
    if (fields) {
      formik.setTouched(setNestedObjectValues(fields, true));
      return false;
    }
    const errors = await formik.validateForm();
    if (Object.keys(errors).length !== 0) {
      let touched = setNestedObjectValues(errors, true) as Record<string, any>;
      formik.setTouched(touched);
      return false;
    }
    return true;
  };

  const handleFormikError = (
    name: string,
    options?: {
      ignoreTouched?: boolean;
      customErrorCheck?: () => boolean;
      parent?: string;
      index?: number;
      deep?: boolean;
    },
  ) => {
    const {
      ignoreTouched = false,
      customErrorCheck,
      parent,
      index,
      deep = false,
    } = options || {};
    if (customErrorCheck && customErrorCheck()) {
      return true;
    }
    const path = buildPath(name, parent, index);
    const error = getIn(formik.errors, path);
    const touched = getIn(formik.touched, path);

    if (deep) {
      return hasAnyError(error, ignoreTouched ? null : touched);
    }
    return Boolean(error && (ignoreTouched || touched));
  };

  const handleFormikHelperText = (
    name: string,
    options?: {
      ignoreTouched?: boolean;
      customErrorCheck?: () => string | null;
      parent?: string;
      index?: number;
      deep?: boolean;
    },
  ) => {
    const {
      ignoreTouched = false,
      customErrorCheck,
      parent,
      index,
      deep = false,
    } = options || {};

    if (customErrorCheck) {
      const customText = customErrorCheck();
      if (customText) return t(customText);
    }
    const path = buildPath(name, parent, index);
    const error = getIn(formik.errors, path);

    if (!handleFormikError(name, { ignoreTouched, parent, index, deep })) {
      return null;
    }

    if (deep) {
      const firstError = getFirstErrorMessage(error);
      return firstError ? t(firstError) : null;
    }

    return t(String(error));
  };

  return {
    validateAndTouch,
    handleFormikError,
    handleFormikHelperText,
  };
};
